import { useState, useMemo } from "react";
import { Calculator, Download, Mail, TrendingUp, Calendar, Info, Copy, Check, BarChart3, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

type UseCase = "cold-email" | "marketing";

interface WarmupWeek {
  week: number;
  dailyMin: number;
  dailyMax: number;
  weeklyTotal: string;
  cumulativeTotal: number;
  focus: string;
  risk: "low" | "medium" | "high";
}

const generateWarmupSchedule = (targetVolume: number, useCase: UseCase, duration: number): WarmupWeek[] => {
  const weeks: WarmupWeek[] = [];
  let cumulativeTotal = 0;
  
  const isColdEmail = useCase === "cold-email";
  
  // Cold email warmup best practices (per mailbox):
  // Shorter warmups = lower safe max volume
  // 2 weeks: max ~20/day, 3 weeks: max ~30/day, 4 weeks: max ~40/day
  // 6 weeks: max ~60/day, 8+ weeks: max 80/day
  const startingDaily = isColdEmail ? 5 : 100;
  
  // Calculate safe max based on duration (longer = higher safe volume)
  let maxDaily: number;
  if (isColdEmail) {
    // Safe max scales with duration: ~10 emails per week of warmup
    maxDaily = Math.min(duration * 10, 80);
  } else {
    maxDaily = targetVolume;
  }
  
  // Linear growth from start to max
  const dailyIncrement = (maxDaily - startingDaily) / Math.max(duration - 1, 1);
  
  for (let week = 1; week <= duration; week++) {
    // Linear growth from starting to max
    let dailyMin = Math.round(startingDaily + dailyIncrement * (week - 1));
    let dailyMax = Math.round(dailyMin * 1.2); // 20% range
    
    // Ensure we don't exceed max
    dailyMin = Math.min(dailyMin, maxDaily);
    dailyMax = Math.min(dailyMax, maxDaily);
    
    // Ensure min is at least the starting value
    dailyMin = Math.max(dailyMin, startingDaily);
    
    const weeklyMin = dailyMin * 7;
    const weeklyMax = dailyMax * 7;
    cumulativeTotal += (weeklyMin + weeklyMax) / 2;
    
    // Risk level based on volume progression
    let risk: "low" | "medium" | "high" = "low";
    const progressRatio = (week - 1) / Math.max(duration - 1, 1);
    if (isColdEmail) {
      if (duration <= 3 && progressRatio > 0.5) risk = "medium"; // Short warmups are riskier
      else if (progressRatio > 0.75) risk = "medium";
      else risk = "low";
    } else {
      if (progressRatio > 0.7) risk = "high";
      else if (progressRatio > 0.3) risk = "medium";
    }
    
    // Focus recommendations based on week position
    let focus = "";
    const weekPosition = week / duration;
    if (isColdEmail) {
      if (weekPosition <= 0.25) focus = "Send to most engaged contacts only";
      else if (weekPosition <= 0.5) focus = "Add recent engagers, monitor bounces";
      else if (weekPosition <= 0.75) focus = "Expand to warm leads, track replies";
      else if (week === duration) focus = "Warmup complete at this level";
      else focus = "Scale carefully, monitor metrics";
    } else {
      if (weekPosition <= 0.25) focus = "Most engaged subscribers only";
      else if (weekPosition <= 0.5) focus = "Add recent openers/clickers";
      else if (weekPosition <= 0.75) focus = "Include 6-12 month engagers";
      else if (week === duration) focus = "Full list coverage achieved";
      else focus = "Expand to dormant but valid contacts";
    }
    
    weeks.push({
      week,
      dailyMin,
      dailyMax,
      weeklyTotal: `${weeklyMin.toLocaleString()}-${weeklyMax.toLocaleString()}`,
      cumulativeTotal: Math.round(cumulativeTotal),
      focus,
      risk,
    });
  }
  
  return weeks;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground mb-1">Week {label}</p>
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            Daily: <span className="font-medium text-primary">{payload[0]?.payload?.dailyMin?.toLocaleString()}-{payload[0]?.payload?.dailyMax?.toLocaleString()}</span>
          </p>
          <p className="text-muted-foreground">
            Cumulative: <span className="font-medium text-success">{payload[1]?.value?.toLocaleString()}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export const DomainWarmupCalculator = () => {
  const [targetVolume, setTargetVolume] = useState<number>(1); // For cold email: number of mailboxes, for marketing: daily volume
  const [useCase, setUseCase] = useState<UseCase>("cold-email");
  const [duration, setDuration] = useState<number>(8);
  const [copied, setCopied] = useState(false);
  
  // Reset target volume when switching use cases
  const handleUseCaseChange = (newUseCase: UseCase) => {
    setUseCase(newUseCase);
    setTargetVolume(newUseCase === "cold-email" ? 1 : 1000);
  };
  
  const schedule = useMemo(() => 
    generateWarmupSchedule(targetVolume, useCase, duration),
    [targetVolume, useCase, duration]
  );

  const chartData = useMemo(() => 
    schedule.map(w => ({
      week: w.week,
      dailyMin: w.dailyMin,
      dailyMax: w.dailyMax,
      dailyAvg: Math.round((w.dailyMin + w.dailyMax) / 2),
      cumulative: w.cumulativeTotal,
      risk: w.risk,
    })),
    [schedule]
  );
  
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "low": return "bg-success/10 text-success border-success/20";
      case "medium": return "bg-warning/10 text-warning border-warning/20";
      case "high": return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted text-muted-foreground";
    }
  };
  
  const handleCopySchedule = () => {
    const scheduleText = schedule.map(w => 
      `Week ${w.week}: ${w.dailyMin}-${w.dailyMax} emails/day | ${w.weeklyTotal}/week | ${w.focus}`
    ).join('\n');
    
    const fullText = `Domain Warmup Schedule\n` +
      `Target: ${targetVolume.toLocaleString()} emails/day\n` +
      `Use Case: ${useCase === 'cold-email' ? 'Cold Email/Sales' : 'Marketing/Newsletter'}\n` +
      `Duration: ${duration} weeks\n\n` +
      scheduleText;
    
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    toast.success("Schedule copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownloadCSV = () => {
    const headers = ["Week", "Daily Min", "Daily Max", "Weekly Total", "Cumulative Total", "Focus", "Risk Level"];
    const rows = schedule.map(w => [
      w.week,
      w.dailyMin,
      w.dailyMax,
      w.weeklyTotal.replace(",", ""),
      w.cumulativeTotal,
      `"${w.focus}"`,
      w.risk
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `warmup-schedule-${targetVolume}-${useCase}-${duration}weeks.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded!");
  };
  
  return (
    <section className="py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-primary" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Warmup Schedule Calculator
          </h2>
        </div>
        <p className="text-muted-foreground">
          Generate a custom warmup schedule based on your target sending volume
        </p>
      </div>
      
      {/* Calculator Inputs */}
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 mb-6">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Target Volume */}
          <div className="space-y-3">
            <Label htmlFor="targetVolume" className="flex items-center gap-2 text-sm font-medium">
              <Mail className="w-4 h-4 text-muted-foreground" />
              {useCase === "cold-email" ? "Number of Mailboxes" : "Target Daily Volume"}
            </Label>
            <Input
              id="targetVolume"
              type="number"
              min={1}
              max={useCase === "cold-email" ? 50 : 100000}
              value={targetVolume}
              onChange={(e) => setTargetVolume(Math.max(1, Math.min(useCase === "cold-email" ? 50 : 100000, parseInt(e.target.value) || 1)))}
              className="text-lg font-semibold"
            />
            <p className="text-xs text-muted-foreground">
              {useCase === "cold-email" 
                ? `${targetVolume} mailbox${targetVolume > 1 ? 'es' : ''} × 80/day = ${(targetVolume * 80).toLocaleString()} emails/day max`
                : `${targetVolume.toLocaleString()} emails per day target`
              }
            </p>
          </div>
          
          {/* Warmup Duration */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              Warmup Duration: {duration} weeks
            </Label>
            <Slider
              value={[duration]}
              onValueChange={(v) => setDuration(v[0])}
              min={2}
              max={12}
              step={1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>2 weeks (very aggressive)</span>
              <span>12 weeks (conservative)</span>
            </div>
          </div>
          
          {/* Use Case Tabs */}
          <div className="space-y-3 sm:col-span-2 lg:col-span-1">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              Use Case
            </Label>
            <Tabs value={useCase} onValueChange={(v) => handleUseCaseChange(v as UseCase)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="cold-email" className="text-xs sm:text-sm">
                  Cold Email
                </TabsTrigger>
                <TabsTrigger value="marketing" className="text-xs sm:text-sm">
                  Marketing
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-xs text-muted-foreground">
              {useCase === "cold-email" 
                ? "Max 80 emails/day per mailbox"
                : "Higher volumes, faster scaling"
              }
            </p>
          </div>
        </div>
        
        {/* Warning Banner for Short Durations */}
        {duration <= 3 && (
          <div className="mt-6 p-4 bg-warning/10 border border-warning/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-warning">⚠️ High Risk Warmup:</span> A {duration}-week warmup is{" "}
              <span className="font-semibold text-foreground">very aggressive</span> and increases the risk of deliverability issues. 
              ISPs may flag rapid volume increases as suspicious behavior. 
              <span className="font-medium text-foreground"> We recommend at least 4-6 weeks</span> for safer warmup.
              Only use short durations if you have extremely high engagement rates (50%+ opens) and pristine list hygiene.
            </div>
          </div>
        )}
        
        {/* Info Banner */}
        <div className="mt-6 p-4 bg-info/5 border border-info/20 rounded-xl flex items-start gap-3">
          <Info className="w-5 h-5 text-info shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            {useCase === "cold-email" ? (
              <>
                <span className="font-medium text-foreground">Cold Email Best Practice:</span> Never exceed{" "}
                <span className="text-primary font-semibold">80 emails/day per mailbox</span>. 
                For higher volumes, use multiple warmed mailboxes. Maintain{" "}
                <span className="text-success font-medium">40%+ open rates</span> and{" "}
                <span className="text-success font-medium">&lt;2% bounce rates</span>.
              </>
            ) : (
              <>
                <span className="font-medium text-foreground">Recommended approach:</span> Start with the minimum daily volume for each week. 
                Only increase if you maintain <span className="text-success font-medium">40%+ open rates</span> and{" "}
                <span className="text-success font-medium">&lt;2% bounce rates</span>.
              </>
            )}
          </div>
        </div>
      </div>

      {/* Warmup Progression Chart */}
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <BarChart3 className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-foreground">
              Volume Progression Curve
            </h3>
            <p className="text-sm text-muted-foreground">
              Visualize your warmup trajectory over {duration} weeks
            </p>
          </div>
        </div>
        
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis 
                dataKey="week" 
                tickFormatter={(v) => `W${v}`}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickLine={false}
              />
              <YAxis 
                yAxisId="daily"
                orientation="left"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                label={{ 
                  value: 'Daily', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: 'hsl(var(--primary))', fontSize: 11 }
                }}
              />
              <YAxis 
                yAxisId="cumulative"
                orientation="right"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                label={{ 
                  value: 'Total', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { fill: 'hsl(var(--success))', fontSize: 11 }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine 
                yAxisId="daily"
                y={useCase === "cold-email" ? Math.min(duration * 10, 80) : targetVolume} 
                stroke="hsl(var(--warning))" 
                strokeDasharray="5 5"
                label={{ 
                  value: useCase === "cold-email" ? `Safe max: ${Math.min(duration * 10, 80)}/day` : 'Target', 
                  position: 'insideTopRight',
                  style: { fill: 'hsl(var(--warning))', fontSize: 11, fontWeight: 600 }
                }}
              />
              <Area
                yAxisId="cumulative"
                type="monotone"
                dataKey="cumulative"
                stroke="hsl(var(--success))"
                strokeWidth={2}
                fill="url(#cumulativeGradient)"
                name="Cumulative Total"
              />
              <Area
                yAxisId="daily"
                type="monotone"
                dataKey="dailyAvg"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#dailyGradient)"
                name="Daily Average"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Chart Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-sm text-muted-foreground">Daily Volume</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-sm text-muted-foreground">Cumulative Total</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-warning" style={{ borderStyle: 'dashed' }} />
            <span className="text-sm text-muted-foreground">
              {useCase === "cold-email" 
                ? `Safe max (${Math.min(duration * 10, 80)}/day for ${duration} weeks)` 
                : `Target (${targetVolume.toLocaleString()}/day)`
              }
            </span>
          </div>
        </div>
      </div>
      
      {/* Generated Schedule */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-semibold text-foreground">
              Your {duration}-Week Warmup Schedule
            </h3>
            <p className="text-sm text-muted-foreground">
              {useCase === "cold-email" 
                ? `Per mailbox warmup to ${Math.min(duration * 10, 80)} emails/day (${targetVolume} mailbox${targetVolume > 1 ? 'es' : ''} = ${(targetVolume * Math.min(duration * 10, 80)).toLocaleString()}/day total)`
                : `Reaching ${targetVolume.toLocaleString()} emails/day by week ${duration}`
              }
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopySchedule}>
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadCSV}>
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
          </div>
        </div>
        
        {/* Schedule Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Week</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Daily Volume</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden sm:table-cell">Weekly Total</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide hidden md:table-cell">Cumulative</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Focus</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {schedule.map((week) => (
                <tr key={week.week} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                      {week.week}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-foreground">
                      {week.dailyMin.toLocaleString()}-{week.dailyMax.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground text-sm ml-1">/day</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                    {week.weeklyTotal}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                    {week.cumulativeTotal.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs">
                    {week.focus}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRiskColor(week.risk)}`}>
                      {week.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Summary Footer */}
        <div className="p-4 sm:p-5 bg-muted/20 border-t border-border">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Starting Volume</div>
              <div className="text-lg font-semibold text-foreground">
                {schedule[0]?.dailyMin.toLocaleString()}/day
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Final Volume</div>
              <div className="text-lg font-semibold text-foreground">
                {schedule[schedule.length - 1]?.dailyMax.toLocaleString()}/day
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Emails</div>
              <div className="text-lg font-semibold text-foreground">
                ~{schedule[schedule.length - 1]?.cumulativeTotal.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Growth Rate</div>
              <div className="text-lg font-semibold text-success">
                {Math.round((schedule[schedule.length - 1]?.dailyMax / schedule[0]?.dailyMin - 1) * 100)}x
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
